import { useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';

import { Flex } from '@chakra-ui/react';
import CircularIconButton from './CircularIconButton';

import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';

const TabListWrapper = ({ step = 200, tabBackground = 'white', children }) => {
    const intl = useIntl();

    const container = useRef(null);
    const [showButtons, setShowButtons] = useState(false);
    const [scrollX, setScrollX] = useState(0);

    useEffect(() => {
        window.onresize = resizeHanlder;
    }, []);

    const resizeHanlder = () => {
        if (container.current && container.current.scrollWidth > container.current.clientWidth) {
            setShowButtons(true);
        } else {
            setShowButtons(false);
        }
    };

    const scrollRight = () => {
        const maxScroll = container.current.scrollWidth - container.current.clientWidth;
        const scrollChange = scrollX + step > maxScroll ? maxScroll : scrollX + step;

        container.current.scrollTo({
            top: 0,
            left: scrollChange,
            behavior: 'smooth',
        });
        setScrollX(scrollChange);
    };

    const scrollLeft = () => {
        const scrollChange = scrollX - step < 0 ? 0 : scrollX - step;

        container.current.scrollTo({
            top: 0,
            left: scrollChange,
            behavior: 'smooth',
        });
        setScrollX(scrollChange);
    };

    const callBackRef = (node) => {
        if (!node) return;

        container.current = node;
        if (node.scrollWidth > node.clientWidth) {
            setShowButtons(true);
        }
    };
    return (
        <TabContainer
            maxWidth="100%"
            scrolledRight={scrollX > 0}
            scrolledLeft={container.current && container.current.clientWidth + scrollX < container.current.scrollWidth}
            tabBackground={tabBackground}
            showButtons={showButtons}
        >
            {showButtons && (
                <CircularIconButtonLeft
                    aria-label={intl.formatMessage({ id: 'common_scroll_left' })}
                    icon={<ChevronDownIcon />}
                    onClick={scrollLeft}
                    disabled={scrollX === 0}
                />
            )}
            <Flex ref={callBackRef} overflow="hidden" p="5px">
                {children}
            </Flex>
            {showButtons && (
                <CircularIconButtonRight
                    aria-label={intl.formatMessage({ id: 'common_scroll_right' })}
                    icon={<ChevronDownIcon />}
                    onClick={scrollRight}
                    disabled={
                        container.current && container.current.scrollWidth - container.current.clientWidth === scrollX
                    }
                />
            )}
        </TabContainer>
    );
};

const TabContainer = styled(Flex, {
    shouldForwardProp: (prop) => !['scrolledLeft', 'scrolledRight', 'showButtons', 'tabBackground'].includes(prop),
})`
    position: relative;

    &:after {
        display: ${(props) => (props.showButtons && props.scrolledLeft ? 'block' : 'none')};
        pointer-events: none;
        content: '';
        width: 50px;
        height: 108%;
        position: absolute;
        right: 50px;
        top: 0;
        background: linear-gradient(270deg, var(--chakra-colors-gray-50) 0%, rgba(250, 250, 250, 0) 100%);
    }

    &:before {
        display: ${(props) => (props.showButtons && props.scrolledRight ? 'block' : 'none')};
        content: '';
        pointer-events: none;
        width: 50px;
        height: 108%;
        position: absolute;
        left: 50px;
        top: 0;
        background: linear-gradient(270deg, rgba(250, 250, 250, 0) 0%, var(--chakra-colors-gray-50) 100%);
    }
`;

const CircularIconButtonLeft = styled(CircularIconButton)`
    transform: rotateZ(90deg);
    margin-right: 15px;

    circle {
        fill: var(--chakra-colors-gray-100);
    }

    &:disabled path {
        fill: var(--chakra-colors-gray-300) !important;
    }
`;
const CircularIconButtonRight = styled(CircularIconButton)`
    transform: rotateZ(-90deg);
    margin-left: 15px;

    circle {
        fill: var(--chakra-colors-gray-100);
    }

    &:disabled path {
        fill: var(--chakra-colors-gray-300) !important;
    }
`;
export default TabListWrapper;
